/**
 * The controls.
 *
 * Every button here is built from the engine's own list of legal actions. Nothing is
 * offered that the engine would reject, and nothing legal is hidden — including the
 * awkward ones, like calling all-in for less than the bet, which a client that
 * reconstructs the rules itself always gets wrong.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Action } from "@engine/types";
import { betRange, sizingShortcuts, legalRaiseTo } from "../lib/table-game";
import { useMoney } from "../lib/MoneyContext";

/* ======================================================= PHASE 8: BLACKJACK ===== */

/** The five things a blackjack player can do. */
export type BjMove = "hit" | "stand" | "double" | "split" | "insurance" | "decline";

/**
 * The blackjack bar's props.
 *
 * WHY A VARIANT AND NOT A SECOND BAR. Poker's bar is built from the engine's `Action`
 * (`{kind, to}`), a pot, a price to call and a raise range, and its body is hooks over
 * that range — none of which a blackjack decision has. What it does have is the same
 * band, the same buttons, the same 44 px targets, the same "nothing the engine would
 * refuse is offered" rule and the same stylesheet (`.abar` / `.abtn`). So `ActionBar`
 * takes a `variant`, and `"blackjack"` renders `BlackjackButtons` inside the same markup;
 * the poker bar is the function it always was. (Phase 9 retired its `"classic"` variant
 * with the classic felt; the top-down bar every caller used is unchanged.)
 */
export interface BjBarProps {
  variant: "blackjack";
  /** Hit / Stand / Double / Split the engine allows AND the stack can cover. */
  legal: readonly ("hit" | "stand" | "double" | "split")[];
  /** The dealer shows an ace and the question is open. */
  insurance: boolean;
  /** The stack covers half a bet. */
  canInsure: boolean;
  /** What a move puts at risk, written for this table ("25 more"). */
  risk?: Partial<Record<BjMove, string>>;
  /** Why a button is off — the spec's "disabled ones stating why". */
  why?: Partial<Record<BjMove, string>>;
  /** Study mode: each move's exact expectation, written, or absent. */
  evs?: Partial<Record<BjMove, string>> | null;
  busy: boolean;
  onMove: (m: BjMove) => void;
}

export function ActionBar(props: PokerBarProps | BjBarProps) {
  return props.variant === "blackjack" ? <BlackjackButtons {...props} /> : <PokerBar {...props} />;
}

function BlackjackButtons({ legal, insurance, canInsure, risk = {}, why = {}, evs, busy, onMove }: BjBarProps) {
  const first = useRef<HTMLButtonElement>(null);
  /* Focus the first live move without scrolling the felt away (poker's reason, verbatim). */
  /* Keyed on the moves' NAMES. The room builds `legal` afresh on every render and it
     re-renders on every clock tick, so keying on the array pulled focus back to Hit once
     a second — off "No insurance" onto "Insurance" for a keyboard player about to press
     Enter, and out of the controls under the felt. */
  const legalKey = legal.join(",");
  useEffect(() => { if (!busy) first.current?.focus({ preventScroll: true }); }, [busy, insurance, legalKey]);
  const has = (m: "hit" | "stand" | "double" | "split") => !insurance && legal.includes(m);
  /* One button, one line under it: the EV in study mode, else what it costs, else why not. */
  const sub = (m: BjMove, on: boolean) => (evs?.[m] ?? (on ? risk[m] : why[m])) ?? null;
  const btn = (m: BjMove, label: string, on: boolean, cls: string, ref?: React.Ref<HTMLButtonElement>) => (
    <button key={m} ref={ref} className={`abtn ${cls}`} disabled={busy || !on}
            data-move={m} onClick={() => onMove(m)}>
      {label}
      {sub(m, on) && <span className="num">{sub(m, on)}</span>}
    </button>
  );
  const firstOn = insurance ? "insurance" : (["hit", "stand", "double", "split"] as const).find((m) => has(m));
  return (
    <div className="abar abar-wide abar-bj">
      <div className="abar-main">
        {btn("hit", "Hit", has("hit"), "abtn-hit", firstOn === "hit" ? first : undefined)}
        {/* During the insurance question Stand's place is "No insurance": the one other answer. */}
        {insurance
          ? btn("decline", "No insurance", true, "abtn-stand")
          : btn("stand", "Stand", has("stand"), "abtn-stand", firstOn === "stand" ? first : undefined)}
        {btn("double", "Double", has("double"), "abtn-double")}
        {btn("split", "Split", has("split"), "abtn-split")}
        {btn("insurance", "Insurance", insurance && canInsure, "abtn-ins", firstOn === "insurance" ? first : undefined)}
      </div>
    </div>
  );
}

/**
 * The bet, beside the bar: 25 · 50 · 100 · 200 within the rack, locked mid-hand.
 *
 * `steps` is what the table offers and `open` how many of them may be chosen — see
 * `BJ_STEPPER_SETTLES` in the room for why that can be fewer than all of them.
 */
export function BetStepper({ value, steps, open = steps.length, max, locked, note, onChange }: {
  value: number;
  steps: readonly number[];
  open?: number;
  /** The stack: no step above it is offered. */
  max: number;
  locked: boolean;
  /** One short line under the amount, or none. */
  note?: string | null;
  onChange: (v: number) => void;
}) {
  const { amount } = useMoney();
  const allowed = steps.slice(0, open).filter((s) => s <= max);
  const i = allowed.indexOf(value);
  const down = i > 0 ? allowed[i - 1]! : null;
  const up = i >= 0 && i < allowed.length - 1 ? allowed[i + 1]! : null;
  /*
   * ONE STOP OPEN: nothing to step between, so no − and + to press. Two dead 44px
   * buttons and a note on a line of their own cost a phone's band fifty pixels to say
   * "25"; flat, it is one line — the amount and the reason, side by side.
   */
  if (open <= 1) {
    return (
      <div className="bk-stepper bk-stepper-flat" role="group" aria-label="Bet">
        <span className="bk-stepper-label">Bet</span>
        <span className="bk-stepper-amt num">{amount(value)}</span>
        {note && <span className="bk-stepper-note">{note}</span>}
      </div>
    );
  }
  return (
    <div className={"bk-stepper" + (locked ? " bk-stepper-locked" : "")} role="group" aria-label="Bet">
      <span className="bk-stepper-label">Bet</span>
      <button type="button" className="bk-step" aria-label="Bet less" disabled={locked || down === null}
              onClick={() => down !== null && onChange(down)}>−</button>
      <span className="bk-stepper-amt num" aria-live="polite">{amount(value)}</span>
      <button type="button" className="bk-step" aria-label="Bet more" disabled={locked || up === null}
              onClick={() => up !== null && onChange(up)}>+</button>
      {/* The stops, as chips — only when there is more than one to choose. */}
      {open > 1 && (
        <span className="bk-stepper-steps">
          {steps.map((s, k) => (
            <button key={s} type="button" className={"bk-stepchip" + (s === value ? " is-on" : "")}
                    disabled={locked || k >= open || s > max} onClick={() => onChange(s)}>{s}</button>
          ))}
        </span>
      )}
      {note && <span className="bk-stepper-note">{note}</span>}
    </div>
  );
}

/* ===================================================================== poker === */

interface PokerBarProps {
  legal: Action[];
  pot: number;
  toCall: number;
  committed: number;
  stack: number;
  chipSize: number;
  bigBlind: number;
  spotId: string;
  onAct: (a: Action) => void;
  busy: boolean;
  /** The one poker bar. Kept as a prop so the union with `"blackjack"` discriminates. */
  variant?: "topdown";
}

function PokerBar({
  legal, pot, toCall, committed, stack, chipSize, bigBlind, spotId, onAct, busy,
}: {
  legal: Action[];
  pot: number;
  toCall: number;
  committed: number;
  stack: number;
  chipSize: number;
  bigBlind: number;
  /**
   * Identifies the decision being made. The sizer resets when it changes.
   *
   * Keying the reset on the slider's endpoints looked equivalent and is not: a hero
   * who checks the flop having put nothing in faces a turn whose minimum and maximum
   * are byte-identical to the flop's, so the effect did not re-run. The sizer stayed
   * open holding a stale amount, and the button read "Bet 60" on a street where the
   * player had intended nothing of the kind.
   */
  spotId: string;
  onAct: (a: Action) => void;
  busy: boolean;
  /**
   * PHASE 9: THE CLASSIC BAR IS RETIRED WITH THE CLASSIC FELT.
   *
   * This took `variant: "classic" | "topdown"`. The classic bar hid the sizer behind a
   * "Raise…" that had to be pressed first and had no all-in button of its own, because
   * in the narrow column under `GameTable` there was nowhere to put them. `GameTable` is
   * gone, and every caller already passed `"topdown"`, so what is left is that bar: the
   * sizing pills and the slider always visible when a raise is legal, and all-in as its
   * own button when the engine offers one. Both are presentation. Nothing here decides
   * what is legal — that list still comes from the engine.
   */
  variant?: "topdown";
}) {
  const { fmt } = useMoney();
  const range = useMemo(() => betRange(legal, committed, stack), [legal, committed, stack]);
  const allShortcuts = useMemo(
    () => sizingShortcuts(pot, toCall, committed, range, chipSize),
    [pot, toCall, committed, range, chipSize]);

  /*
   * All-in has its own button, so the pill that sets the slider to the same number is a
   * second control for one action sitting a centimetre from the first. It goes.
   */
  const shortcuts = useMemo(
    () => allShortcuts.filter((s) => s.label !== "all in"),
    [allShortcuts]);
  /*
   * Phase 6, box 0(2): the sizer never HOLDS a fraction, not just never emits one.
   *
   * `legalRaiseTo` already rounds to the unit and clamps after rounding, and the
   * raise button has always gone through it. The slider did not: its value came
   * straight off the input, so a felt could read "Bet 470.50" on a button that would
   * have emitted 470. Snapping on the way IN makes the number on the button the
   * number that is sent, which is the only version a player can check.
   */
  const snapTo = (v: number) => (range.canRaise ? legalRaiseTo(v, range, chipSize) : v);
  const [to, setTo] = useState(() => snapTo(range.min));
  const firstRef = useRef<HTMLButtonElement>(null);

  /** Reset the slider whenever the spot changes. */
  useEffect(() => { setTo(snapTo(range.min)); }, [spotId, range.min]);

  /** The sizer is not a disclosure, so it never has to be opened. */
  const sizerOpen = range.canRaise;

  /*
   * Focus the first action, and on the wide felt do it WITHOUT scrolling.
   *
   * Giving Fold the focus each time the action arrives is how the table is played from
   * a keyboard, and it stays. What has to change on the top-down layout is the scroll
   * that comes free with it: the bar is inside the felt, near its bottom edge, so
   * bringing it into view drags the top of the table off the screen — measured at 374px
   * on the desktop route and 243px on a phone, which is the top three seats gone the
   * moment it is your turn.
   */
  useEffect(() => { if (!busy) firstRef.current?.focus({ preventScroll: true }); }, [busy, legal]);

  if (legal.length === 0) return null;

  const has = (k: Action["kind"]) => legal.some((a) => a.kind === k);
  const callAction = legal.find((a) => a.kind === "call");
  const allInAction = legal.find((a) => a.kind === "allin");

  const raiseNow = () => {
    // One helper decides what is legal, so this cannot disagree with the shortcuts
    // above it or with the engine below it.
    const target = legalRaiseTo(to, range, chipSize);
    onAct(target >= range.max
      ? (allInAction ?? { kind: range.kind, to: target })
      : { kind: range.kind, to: target });
  };

  return (
    <div className="abar abar-wide">
      <div className="abar-main">
        {has("fold") && (
          <button ref={firstRef} className="abtn abtn-fold" disabled={busy}
                  onClick={() => onAct({ kind: "fold" })}>
            Fold
          </button>
        )}
        {has("check") && (
          <button className="abtn abtn-check" disabled={busy}
                  onClick={() => onAct({ kind: "check" })}>
            Check
          </button>
        )}
        {callAction && (
          <button className="abtn abtn-call" disabled={busy}
                  onClick={() => onAct(callAction)}>
            Call <span className="num">{fmt(Math.min(toCall, stack))}</span>
            {toCall >= stack && <em> (all in)</em>}
          </button>
        )}
        {range.canRaise && (
          <button className="abtn abtn-raise" disabled={busy}
                  onClick={raiseNow}>
            {range.kind === "bet" ? "Bet" : "Raise to"} <span className="num">{fmt(to)}</span>
          </button>
        )}
        {/*
          * All in, as its own button, on the wide felt only.
          *
          * It is the engine's own `allin` action — this offers nothing the engine did
          * not put in `legal`, and it is hidden rather than disabled when the engine
          * did not, because a table that shows a control it will refuse teaches the
          * player the wrong rules.
          */}
        {allInAction && (
          <button className="abtn abtn-allin" disabled={busy}
                  onClick={() => onAct(allInAction)}>
            All in <span className="num">{fmt(stack + committed)}</span>
          </button>
        )}
      </div>

      {sizerOpen && range.canRaise && (
        <div className="abar-size">
          <div className="abar-shortcuts">
            {shortcuts.map((s) => (
              <button key={s.label} className={`achip${Math.abs(s.to - to) < 1e-9 ? " achip-on" : ""}`}
                      disabled={busy} onClick={() => setTo(s.to)}>
                {s.label}
              </button>
            ))}
          </div>
          <label className="abar-slider">
            <span className="sr-only">Raise amount</span>
            <input
              type="range"
              min={range.min}
              max={range.max}
              step={chipSize > 0 ? chipSize : bigBlind / 100}
              /* The ends are the engine's own legal amounts; the STEPS are whole units. */
              value={to}
              disabled={busy}
              onChange={(e) => setTo(snapTo(Number(e.target.value)))}
            />
            <span className="abar-amount num">
              {fmt(to)} <em>({fmt(Math.max(0, to - committed))} more)</em>
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
